import axios from "axios";
import {
  notifyOrgTokensRefreshed,
  type RefreshedOrgTokens,
} from "./authTokenBridge";
import { normalizeApiBaseUrl } from "../../utils/apiBaseUrl";
import { uploadFileToPresignedUrl } from "../../utils/presignedUpload";

const rawApiBase =
  process.env.REACT_APP_NOTICBOARD_API ||
  "https://glexr3s2z3.execute-api.us-west-2.amazonaws.com/dev";

export const API_BASE_URL = normalizeApiBaseUrl(rawApiBase);

export const DATA_URL_IMAGE_REGEX = /^data:(image\/[a-zA-Z0-9.+-]+);base64,/;

export const getImageExtensionFromContentType = (contentType: string): string => {
  switch (contentType.toLowerCase()) {
    case "image/jpeg":
      return "jpg";
    case "image/jpg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "image/gif":
      return "gif";
    case "image/heic":
      return "heic";
    case "image/heif":
      return "heif";
    default:
      return "img";
  }
};

export const dataUrlToBlob = (
  dataUrl: string
): { blob: Blob; contentType: string } => {
  if (!DATA_URL_IMAGE_REGEX.test(dataUrl)) {
    throw new Error("Invalid image data URL");
  }

  const firstCommaIndex = dataUrl.indexOf(",");
  if (firstCommaIndex === -1) {
    throw new Error("Invalid image data URL");
  }

  const header = dataUrl.substring(0, firstCommaIndex);
  const base64 = dataUrl.substring(firstCommaIndex + 1);

  const contentTypeMatch = header.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64$/);
  const contentType = contentTypeMatch?.[1];
  if (!contentType) {
    throw new Error("Invalid image content type");
  }

  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return { blob: new Blob([bytes], { type: contentType }), contentType };
};

async function tryRefreshOrgCognitoTokens(
  baseUrl: string
): Promise<RefreshedOrgTokens | null> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return null;
  }

  const res = await fetch(`${baseUrl}/v1/org/auth/refresh-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    return null;
  }

  const data = (await res.json().catch(() => null)) as {
    tokens?: { idToken?: string; refreshToken?: string };
  } | null;

  const idToken = data?.tokens?.idToken;
  if (!idToken || typeof idToken !== "string") {
    return null;
  }

  const nextRefresh = data.tokens?.refreshToken || refreshToken;
  localStorage.setItem("token", idToken);
  localStorage.setItem("refreshToken", nextRefresh);
  const out: RefreshedOrgTokens = { idToken, refreshToken: nextRefresh };
  notifyOrgTokensRefreshed(out);
  return out;
}

let refreshInFlight: Promise<RefreshedOrgTokens | null> | null = null;

async function refreshOrgCognitoTokensSingleFlight(
  baseUrl: string
): Promise<RefreshedOrgTokens | null> {
  if (!refreshInFlight) {
    refreshInFlight = tryRefreshOrgCognitoTokens(baseUrl).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function shouldForceLogoutOnAuthFailure(
  status: number,
  errorMessage: string | undefined
): boolean {
  // 401 should always terminate the current session.
  if (status === 401) return true;

  // 403 can be either token/auth related OR a valid permission denial.
  // Only force logout for auth/token-expiry style errors.
  if (status !== 403) return false;
  const normalized = (errorMessage || "").toLowerCase();
  return (
    normalized.includes("session has expired") ||
    normalized.includes("token") ||
    normalized.includes("unauthorized") ||
    normalized.includes("not authenticated") ||
    normalized.includes("expired") ||
    normalized.includes("invalid signature")
  );
}

/** A failed request: the message as before, plus the status and the response body (e.g. per-row problems). */
export type ApiRequestError = Error & { status?: number; responseBody?: unknown };

export const makeRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
  baseUrl?: string,
  allowAuthRefreshRetry = true
): Promise<T> => {
  const url = normalizeApiBaseUrl(baseUrl || API_BASE_URL);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers
      ? (options.headers as Record<string, string>)
      : {}),
  };

  const response = await fetch(`${url}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle auth failures - Cognito expiry can surface as 401 or 403 depending on route/gateway behavior.
    const hasAuthHeader = Boolean(headers.Authorization || headers.authorization);
    if ((response.status === 401 || response.status === 403) && allowAuthRefreshRetry && hasAuthHeader) {
      // 401 always attempts token refresh first.
      if (response.status === 401) {
        const refreshed = await refreshOrgCognitoTokensSingleFlight(url);
        if (refreshed) {
          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${refreshed.idToken}`,
          };
          return makeRequest<T>(
            endpoint,
            { ...options, headers: retryHeaders },
            baseUrl,
            false
          );
        }
      } else {
        // 403 can be either permission denied OR auth/token-expiry. Only refresh for auth-style 403s.
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.details ||
          errorData?.error ||
          errorData?.message ||
          "Access denied.";
        const authLike403 = shouldForceLogoutOnAuthFailure(403, errorMessage);

        if (authLike403) {
          const refreshed = await refreshOrgCognitoTokensSingleFlight(url);
          if (refreshed) {
            const retryHeaders = {
              ...headers,
              Authorization: `Bearer ${refreshed.idToken}`,
            };
            return makeRequest<T>(
              endpoint,
              { ...options, headers: retryHeaders },
              baseUrl,
              false
            );
          }
        }

        if (authLike403) {
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");

          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        throw new Error(errorMessage);
      }

      const errorData = await response.json().catch(() => null);
      const errorMessage =
        errorData?.details ||
        errorData?.error ||
        errorData?.message ||
        "Your session has expired. Please log in again.";

      if (shouldForceLogoutOnAuthFailure(response.status, errorMessage)) {
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
      throw new Error(errorMessage);
    }

    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.details ||
      errorData?.error ||
      errorData?.message ||
      `API request failed: ${response.statusText}`;
    const failure: ApiRequestError = Object.assign(new Error(errorMessage), {
      status: response.status,
      responseBody: errorData,
    });
    throw failure;
  }

  return response.json();
};

export const getPresignedUploadUrl = async (
  fileName: string,
  contentType: string,
  folderId: string,
  token: string
): Promise<{ presignedUrl: string; fileUrl: string; fileKey: string }> => {
  return makeRequest<{
    presignedUrl: string;
    fileUrl: string;
    fileKey: string;
  }>("/v1/notices/presigned-url", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      fileName,
      contentType,
      noticeId: folderId,
    }),
  });
};

export const uploadImageDataUrlAndReturnUrl = async (
  dataUrl: string,
  token: string,
  folderId: string,
  fileNameHint: string
): Promise<string> => {
  const { blob, contentType } = dataUrlToBlob(dataUrl);
  const ext = getImageExtensionFromContentType(contentType);
  const safeName = fileNameHint.replace(/[^a-zA-Z0-9.-]/g, "_");
  const fileName = safeName.includes(".") ? safeName : `${safeName}.${ext}`;

  const { presignedUrl, fileUrl } = await getPresignedUploadUrl(
    fileName,
    contentType,
    folderId,
    token
  );

  await uploadFileToPresignedUrl({
    presignedUrl,
    file: blob,
    contentType,
  });

  return fileUrl;
};

// Helper function to validate notice types and get base type
export const getValidNoticeType = (
  type: string | undefined
): "info" | "event" | "image" => {
  if (!type) return "info";
  const validType = (type as string).toLowerCase();
  // Map "urgent" to "info" for backward compatibility (urgent is now a flag, not a type)
  if (validType === "urgent") return "info";
  if (validType === "direct") return "info";
  // Normalize image_notice to image
  if (validType === "image_notice") return "image";
  // Valid base types
  if (validType === "info" || validType === "event" || validType === "image") {
    return validType;
  }
  return "info";
};

// Re-export axios for modules that need it
export { axios };
